import escapeRegExp from 'escape-string-regexp';

export function isInsideFolder(path: string, folderPath: string) {
  return path.startsWith(`${folderPath}/`);
}

export function getParentPath(path: string): string | undefined {
  return path.includes('/') ? path.replace(/\/[^/]+$/, '') : undefined;
}

export function changeParentPath(path: string, oldParentPath: string, newParentPath: string) {
  return path.replace(new RegExp('^' + escapeRegExp(oldParentPath + '/')), newParentPath + '/');
}

export function getUniquePath(allPaths: string[], suggestedPath: string, initialSuffix?: string) {
  const parts = suggestedPath.includes('.') ? suggestedPath.split('.') : undefined;
  const ext = parts ? parts.pop() : '';
  const initialPath = parts ? parts.join('.') : suggestedPath;

  let path = suggestedPath;
  let counter = initialSuffix ? 0 : 1;

  while (allPaths.some((p) => p.toLowerCase() === path.toLowerCase())) {
    const suffix = `${initialSuffix ?? ''} ${counter || ''}`.trim();

    if (ext) {
      path = `${initialPath} ${suffix}.${ext}`;
    } else {
      path = `${initialPath} ${suffix}`;
    }

    counter++;
  }

  return path;
}

export function isEntryPoint(name: string): boolean {
  return /^App\.(js|tsx?)$/.test(name);
}

/**
 * Candidate entry files, MOST preferred first. TypeScript wins over JavaScript:
 * Tile apps are generated as TS, so when an app somehow has both, `App.tsx` is
 * the one the user means. Root beats `src/`, and an `App.*` beats an `index.*`.
 */
const ENTRY_FILE_CANDIDATES = [
  'App.tsx',
  'App.jsx',
  'App.ts',
  'App.js',
  'src/App.tsx',
  'src/App.jsx',
  'src/App.ts',
  'src/App.js',
  'index.tsx',
  'index.jsx',
  'index.ts',
  'index.js',
];

/**
 * The file the editor should open by default — the app's entry point, if it has
 * one. Returns `null` when no candidate is present, and callers must respect
 * that: opening an arbitrary first file lands the user on `app.json` or a PNG.
 *
 * This is the SINGLE definition of "the entry file". It previously existed as
 * four divergent copies (App.tsx's selectedFile ladder, EditorViewV2's Screens
 * setup, `isEntryPoint`, navigationRootDetector's ENTRY_FILES) with different
 * orders and coverage — which is how a hardcoded `App.js` survived in an app
 * whose entry is `App.tsx`.
 */
export function resolveEntryFile(
  files: Record<string, { type?: string; contents?: unknown } | undefined | null>,
): string | null {
  const isCode = (path: string) => {
    const f = files[path];
    return !!f && f.type !== 'ASSET' && typeof f.contents === 'string';
  };
  return ENTRY_FILE_CANDIDATES.find(isCode) ?? null;
}

const PROTECTED_FILES = [
  'app.json',
  'package.json',
  'babel.config.js',
  'metro.config.js',
  'tsconfig.json',
  'src/app/_layout.tsx',
];

export function isProtectedFile(path: string): boolean {
  return PROTECTED_FILES.includes(path) || isEntryPoint(path);
}

export function isPackageJson(name: string): boolean {
  return name === 'package.json';
}

export function isESLintConfig(name: string): boolean {
  return /^\.eslintrc(\.json)?$/.test(name);
}

export function isImage(name: string): boolean {
  return /\.(bmp|jpg|jpeg|png|gif|svg|webp)$/.test(name);
}

export function isScript(name: string): boolean {
  return /\.(js|tsx?)$/.test(name);
}

export function isJson(name: string): boolean {
  return name.endsWith('.json');
}

export function isMarkdown(name: string): boolean {
  return name.endsWith('.md');
}

export function isAsset(name: string): boolean {
  return !(isScript(name) || isJson(name) || isMarkdown(name));
}

export function isJS(name: string): boolean {
  return name.endsWith('.js');
}

export function isTS(name: string): boolean {
  return isScript(name) && !isJS(name);
}

export function isTest(name: string): boolean {
  name = name.toLocaleLowerCase();
  return (
    name.includes('__tests__') ||
    name.includes('__integration-tests__') ||
    name.includes('__mocks__') ||
    name.includes('.test') ||
    name.includes('.spec')
  );
}
